import { createContext, useContext, useEffect } from 'react';

// Lets a page put its own name in the phone's top bar (MobileHeader), for
// example the open project's name instead of "Projects".
export const MobileTitleContext = createContext(() => {});

/** Show `title` in the phone's top bar while the calling page is mounted. */
export function useMobileTitle(title) {
  const setTitle = useContext(MobileTitleContext);
  useEffect(() => {
    if (!title) return undefined;
    setTitle(title);
    return () => setTitle(null);
  }, [title, setTitle]);
}

/** A chat (/app/chats/<id>, /app/chats/new; an older link to a bot's page on its way there): it
 * fills the screen edge to edge and, on phones, hides the top bar and the tab bar (a full-screen
 * conversation with its own header, whose back arrow leads to the history). */
export const isChatScreen = (pathname) =>
  /^\/app\/bots\/(?!scheduled$)[^/]+$/.test(pathname) || /^\/app\/chats\/[^/]+$/.test(pathname);

/** An open document (/app/documents/<id>): it is exactly as tall as the content panel, so a
 * whiteboard, mind map or table fills it and only the document scrolls, never the page around
 * it (a mind map moved with the page while being drawn on a phone). */
export const isDocumentScreen = (pathname) => /^\/app\/documents\/[^/]+$/.test(pathname);

// Top-level screens (bottom tabs: Home, Tasks, Chats, Projects) show a large title and no
// back arrow; every other app page is a sub-screen with a back arrow to `parent`. The
// Planner is the Tasks' calendar; managing bots is reached from the chats.
const SCREENS = [
  { match: /^\/app(\/agenda)?$/, home: true },
  { match: /^\/app\/(tasks|planner)$/, key: 'nav.tasks', root: true },
  { match: /^\/app\/projects$/, key: 'nav.projects', root: true },
  { match: /^\/app\/chats$/, key: 'nav.chats', root: true },
  { match: /^\/app\/chats\/[^/]+$/, key: 'nav.chats', parent: '/app/chats' },
  { match: /^\/app\/bots$/, key: 'nav.manage_bots', parent: '/app/chats' },
  { match: /^\/app\/bots\/scheduled$/, key: 'bots.scheduled.title', parent: '/app/bots' },
  { match: /^\/app\/bots\/[^/]+(\/chats)?$/, key: 'nav.chats', parent: '/app/chats' },
  { match: /^\/app\/documents$/, key: 'nav.documents' },
  { match: /^\/app\/documents\/.+/, key: 'nav.documents', parent: '/app/documents' },
  { match: /^\/app\/habits$/, key: 'nav.habits' },
  { match: /^\/app\/spotlight$/, key: 'spotlight.title' },
  { match: /^\/app\/focus-time$/, key: 'nav.focus_time' },
  { match: /^\/app\/buffer-time$/, key: 'nav.buffer_time' },
  { match: /^\/app\/analytics$/, key: 'nav.analytics' },
  { match: /^\/app\/teams$/, key: 'nav.teams' },
  { match: /^\/app\/webhooks$/, key: 'nav.webhooks' },
  { match: /^\/app\/settings$/, key: 'nav.settings' },
  { match: /^\/app\/(subscription|billing)$/, key: 'nav.billing' },
  { match: /^\/app\/trash$/, key: 'nav.trash' },
  { match: /^\/app\/notifications$/, key: 'notifications.title' },
  { match: /^\/app\/help$/, key: 'nav.help' },
  { match: /^\/app\/admin$/, key: 'nav.admin' },
  { match: /^\/app\/gmail$/, key: 'nav.email' },
];

/** What the phone's top bar shows on `pathname` (`search` tells an open project from the list). */
export function mobileScreen(pathname, search) {
  if (pathname === '/app/projects' && new URLSearchParams(search).get('project')) {
    return { key: 'nav.projects', parent: '/app/projects' };
  }
  const screen = SCREENS.find((s) => s.match.test(pathname));
  return screen || { key: null, parent: '/app' };
}
